import { Add } from '@mui/icons-material';
import { TabContext, TabPanel } from '@mui/lab';
import {
  Button,
  Card,
  Tab,
  Tabs,
  Typography,
  Box,
  Select,
  MenuItem,
  Tooltip,
  FormControl,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import contractItemsColumnArray from './content/contractItemsColumnArray';
import calculateAndEnrichItems from './content/calculateAndEnrichItems';
import ContentEditDialog from './content/ContentEditDialog';
import { toast } from 'react-toastify';
import SalesBillOfMaterialGrid from './content/SalesBillOfMaterialGrid';
import SupportTicketDialog from './content/SupportTicketDialog';
import Comments from '@/components/toasts/Comments';
import useApiQuery from '@/hooks/useApiQuery';
import useApiMutation from '@/hooks/useApiMutation';
import useApiDelete from '@/hooks/useApiDelete';
import files from '@/services/FileService';
import { formatBytes } from '@/utils/formatBytes';
import {
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarningIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import { useRouteLoaderData } from 'react-router-dom';
import History from '@/components/toasts/History';

export default function ContractContent({
  customerId,
  source,
  contract,
  onContractChange,
  saveContract,
  isLoading,
  isPricingLoading,
  hasContractChanged,
}) {
  const [tabValue, setTabValue] = useState('overview');
  const [isSalesBOMItem, setIsSalesBOMItem] = useState(false);
  const [isSupportTicket, setIsSupportTicket] = useState(false);
  const [selectedEditItem, setSelectedEditItem] = useState(null);
  const [selectedFormState, setSelectedFormState] = useState(null);
  const commentsRef = useRef();

  const [isDocsDialogOpen, setIsDocsDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState({});
  const {
    fixedDeploymentConfigs: { contracts },
    me,
  } = useRouteLoaderData('auth');

  const mongoFilterDocs = useMemo(() => {
    return {
      $and: [
        {
          filename: {
            $regex: `^contracts/${contract?._id ? `${contract?._id}/` : 'NO_ID'}`,
            $options: 'i',
          },
        },
      ],
    };
  }, [contract?._id]);

  const {
    data: documentFiles = [],
    isPending: isDocsPending,
    refetch: refetchDocs,
  } = useApiQuery('file', mongoFilterDocs, !!contract?._id);
  const { mutateAsync: createFile } = useApiMutation('file');
  const { mutateAsync: updatedFile } = useApiMutation();
  const { mutateAsync: deleteFile } = useApiDelete('file');

  const [uploadRows, setUploadRows] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isDocsDialogOpen) {
      setUploadRows([]);
      setIsDragActive(false);
      setIsUploading(false);
    }
  }, [isDocsDialogOpen]);

  const getUniqueName = useCallback(
    (baseName, currentRows = []) => {
      const parts = baseName.split('.');
      const ext = parts.length > 1 ? '.' + parts.pop() : '';
      const name = parts.join('.');
      let i = 1;
      let candidate = `${name}_${i}${ext}`;
      const fullPathCandidate = (fname) =>
        `contracts/${contract?._id}/${fname}`;

      while (
        documentFiles.some(
          (f) => f.filename === fullPathCandidate(candidate),
        ) ||
        currentRows.some(
          (r) =>
            r.action !== 'overwrite' &&
            (r.targetName === candidate ||
              (r.targetName === baseName && baseName === candidate)),
        )
      ) {
        i += 1;
        candidate = `${name}_${i}${ext}`;
      }
      return candidate;
    },
    [documentFiles, contract?._id],
  );

  const processFiles = (filesList) => {
    if (!filesList || filesList.length === 0) return;

    const newRows = Array.from(filesList)
      .map((file, index) => {
        const fullPath = `contracts/${contract?._id}/${file.name}`;
        const existingServerFile = documentFiles.find(
          (df) => df.filename === fullPath,
        );
        const existingQueueItem = uploadRows.find(
          (r) => r.file.name === file.name,
        );
        if (existingQueueItem) return null;
        const hasCollision = !!existingServerFile;
        const action = hasCollision ? 'rename' : 'new';
        let targetName = file.name;
        if (action === 'rename') {
          targetName = getUniqueName(file.name, uploadRows);
        }

        return {
          id: uuidv4(),
          file,
          originalName: file.name,
          size: file.size,
          hasCollision,
          existingFileId: existingServerFile?._id,
          action,
          targetName,
        };
      })
      .filter(Boolean);
    setUploadRows((prev) => {
      return [...prev, ...newRows];
    });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragActive && !isUploading) setIsDragActive(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (isUploading) return;

    const dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length) processFiles(dt.files);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
    e.target.value = null;
  };

  const updateRowAction = (id, newAction) => {
    setUploadRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        let newTargetName = row.originalName;
        if (newAction === 'rename') {
          const otherRows = prev.filter((r) => r.id !== id);
          newTargetName = getUniqueName(row.originalName, otherRows);
        }

        return {
          ...row,
          action: newAction,
          targetName: newTargetName,
        };
      }),
    );
  };

  const removeUploadRow = (id) => {
    setUploadRows((prev) => prev.filter((r) => r.id !== id));
  };

  const processUploads = async () => {
    if (uploadRows.length === 0) return;
    setIsUploading(true);

    try {
      for (const row of uploadRows) {
        const finalFilename = `contracts/${contract?._id}/${row.targetName}`;

        if (row.action === 'overwrite' && row.existingFileId) {
          await updatedFile(
            {
              _id: row.existingFileId,
              size: row.file.size,
              sharedWith: [
                {
                  type: 'tenant',
                  tenant: me.tenant,
                  permissions: { read: true, update: true, delete: true },
                },
              ],
            },
            'file',
            row.existingFileId,
          );
          const formData = new FormData();
          formData.append('file', row.file);
          await files.post(`/${row.existingFileId}`, formData);
        } else {
          const fileRes = await createFile(
            {
              filename: finalFilename,
              size: row.file.size,
              sharedWith: [...(contracts?.contractShares || [])],
            },
            undefined,
            undefined,
            false,
          );

          let fileId =
            fileRes?.insertedId ||
            (fileRes?.acknowledged ? fileRes.insertedId : null);
          if (!fileId && fileRes?._id) fileId = fileRes?._id;

          if (fileId) {
            const formData = new FormData();
            formData.append('file', row.file);
            await files.post(`/${fileId}`, formData);
          }
        }
      }

      toast.success(`${uploadRows.length} Dateien erfolgreich verarbeitet.`);
      setUploadRows([]);
      refetchDocs();
      setIsDocsDialogOpen(false);
    } catch (err) {
      console.error('Fehler beim Upload:', err);
      toast.error('Fehler beim Hochladen der Dateien.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadFile = async (file) => {
    setDownloading((prev) => ({ ...prev, [file?._id]: true }));
    try {
      const fileResponse = await files.get(`/${file?._id}`, {
        responseType: 'blob',
      });
      if (!fileResponse?.data) throw new Error('Datei nicht vorhanden!');
      const blob = new Blob([fileResponse.data], {
        type: 'application/octet-stream',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${file?.filename?.split('/').pop()}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Herunterladen der Datei');
    } finally {
      setDownloading((prev) => ({ ...prev, [file?._id]: false }));
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await deleteFile(fileId);
      toast.success('Datei gelöscht');
      refetchDocs();
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Löschen der Datei');
    }
  };

  const { rows, overflowSources } = useMemo(() => {
    return calculateAndEnrichItems(contract?.contractItems, source, new Date());
  }, [contract, source]);

  const combinedSourceOptions = useMemo(() => {
    return [...(source || []), ...(overflowSources || [])];
  }, [source, overflowSources]);

  const getDetailPanelContent = useCallback(
    ({ row: parentRow, id: parentId }) => {
      const parentIndex = rows.findIndex((r) => r.id === parentId);
      if (
        parentRow.articleType === 'SALES_BILL_OF_MATERIAL' &&
        parentRow.salesBillOfMaterialItems
      ) {
        return (
          <SalesBillOfMaterialGrid
            parentRow={parentRow}
            parentIndex={parentIndex}
            isPricingLoading={isPricingLoading}
            setSelectedEditItem={setSelectedEditItem}
            setIsSalesBOMItem={setIsSalesBOMItem}
            setIsSupportTicket={setIsSupportTicket}
          />
        );
      }
      return null;
    },
    [rows],
  );

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  useEffect(() => {
    if (selectedEditItem === 'new') {
      setSelectedFormState({
        uuid: uuidv4(),
        title: '',
        description: '',
        articleId: null,
        articleNumber: '',
        quantity: { identifiers: [] },
        unitPrice: 0,
        discountPercentage: 0,
        pricingDate: new Date(),
        rounding: 'EXACT',
        type: 'DYNAMIC',
        startDate: null,
        endDate: null,
      });
    } else if (
      selectedEditItem !== null &&
      contract?.contractItems &&
      !isSalesBOMItem
    ) {
      const itemToEdit = contract.contractItems.find(
        (item) => item.uuid === selectedEditItem,
      );
      setSelectedFormState(JSON.parse(JSON.stringify(itemToEdit)));
    } else if (
      selectedEditItem !== null &&
      contract?.contractItems &&
      isSalesBOMItem
    ) {
      const { parentIndex, subIndex } = selectedEditItem;
      const subItemToEdit =
        contract.contractItems[parentIndex].salesBillOfMaterialItems[subIndex];
      setSelectedFormState(JSON.parse(JSON.stringify(subItemToEdit)));
    }
  }, [selectedEditItem, contract]);

  const addLinkedDocumentToContract = (newDocument) => {
    const updatedContract = {
      ...contract,
      linkedDocuments: [...(contract.linkedDocuments || []), newDocument],
    };
    onContractChange(updatedContract);
    saveContract(updatedContract);
  };

  const handleSave = () => {
    if (!selectedEditItem) return;
    if (!selectedFormState.uuid) selectedFormState.uuid = uuidv4();
    const updatedContract = { ...contract };

    if (isSalesBOMItem) {
      const { parentIndex, subIndex } = selectedEditItem;

      const updatedItems = JSON.parse(JSON.stringify(contract.contractItems));
      updatedItems[parentIndex].salesBillOfMaterialItems[subIndex] =
        selectedFormState;
      updatedContract.contractItems = updatedItems;
    } else {
      if (selectedFormState.salesBillOfMaterialItems) {
        selectedFormState.salesBillOfMaterialItems.forEach((si) => {
          if (!si.uuid) si.uuid = uuidv4();
        });
      }
      let updatedItems;
      if (selectedEditItem === 'new') {
        updatedItems = [...(contract?.contractItems || []), selectedFormState];
      } else {
        updatedItems = [...contract.contractItems];
        const indexToUpdate = updatedItems.findIndex(
          (item) => item.uuid === selectedEditItem,
        );
        if (indexToUpdate !== -1) {
          updatedItems[indexToUpdate] = selectedFormState;
        }
      }
      updatedContract.contractItems = updatedItems;
    }

    onContractChange(updatedContract, {
      uuid: selectedFormState.uuid,
      isSubItem: isSalesBOMItem,
    });
    setSelectedEditItem(null);
    setIsSalesBOMItem(false);
    setIsSupportTicket(false);
  };

  const deleteItem = useCallback(
    (uuidToDelete) => {
      setSelectedEditItem(null);
      setIsSalesBOMItem(false);
      const updatedItems = (contract?.contractItems || []).filter(
        (item) => item.uuid !== uuidToDelete,
      );
      onContractChange({ ...contract, contractItems: updatedItems });
    },
    [contract, onContractChange],
  );

  const handleRowOrderChange = (params) => {
    const movedItemUuid = params.row.uuid;
    const targetIndex = params.targetIndex;
    const currentItems = contract.contractItems || [];

    const movedItem = currentItems.find((item) => item.uuid === movedItemUuid);
    if (!movedItem) return;

    const remainingItems = currentItems.filter(
      (item) => item.uuid !== movedItemUuid,
    );
    const prospectiveNewOrder = [...remainingItems];
    prospectiveNewOrder.splice(targetIndex, 0, movedItem);

    let isOrderValid = true;
    let errorMessage = '';

    const itemIndexMap = new Map(
      prospectiveNewOrder.map((item, index) => [item.uuid, index]),
    );

    for (let i = 0; i < prospectiveNewOrder.length; i++) {
      const currentItem = prospectiveNewOrder[i];
      const identifiers = currentItem.quantity?.identifiers || [];

      for (const identifier of identifiers) {
        if (identifier.startsWith('overflow_')) {
          const parts = identifier?.split('_');
          if (parts.length < 3) continue;

          const producerUuid = parts[1];
          const producerIndex = itemIndexMap.get(producerUuid);

          if (producerIndex > i) {
            isOrderValid = false;
            const producerItem = prospectiveNewOrder.find(
              (p) => p.uuid === producerUuid,
            );
            errorMessage = `Verschieben nicht möglich. Diese Position hängt vom Überlauf aus "${producerItem?.title || 'einer anderen Position'}" ab und muss danach platziert werden.`;
            break;
          }
        }
      }
      if (!isOrderValid) break;
    }

    if (isOrderValid) {
      onContractChange({ ...contract, contractItems: prospectiveNewOrder });
    } else {
      toast.error(errorMessage);
      onContractChange({ ...contract });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Card
        sx={{
          width: '100%',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
            Vertragsinhalte
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {tabValue === 'overview' && (
            <>
              <Button
                variant="outlined"
                onClick={async () => {
                  await saveContract(contract, true);
                }}
                sx={{ mr: 1 }}
                disabled={isLoading || !hasContractChanged}
              >
                Speichern
              </Button>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setSelectedEditItem('new')}
                disabled={isLoading}
              >
                Hinzufügen
              </Button>
            </>
          )}
          {tabValue === 'documents' && (
            <Button
              variant="outlined"
              startIcon={<CloudUploadOutlinedIcon />}
              onClick={() => setIsDocsDialogOpen(true)}
              disabled={isLoading}
            >
              Hinzufügen
            </Button>
          )}
          {tabValue === 'comments' && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() =>
                commentsRef.current && commentsRef.current.openDialog()
              }
            >
              Hinzufügen
            </Button>
          )}
        </Box>
        <TabContext value={tabValue}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}
          >
            <Tab label="Übersicht" value="overview" />
            <Tab label="Dokumente" value="documents" />
            <Tab label="Kommentare" value="comments" />
            <Tab label="Tickets" value="tickets" />
            <Tab label="Historie" value="history" />
          </Tabs>
          <TabPanel value="overview" sx={{ p: 0, mt: 2 }}>
            <DataGridPro
              rows={rows}
              columns={contractItemsColumnArray(
                getDetailPanelContent,
                source,
                setSelectedEditItem,
                isPricingLoading,
                setIsSalesBOMItem,
                setIsSupportTicket,
              )}
              disableRowSelectionOnClick
              rowReordering
              onRowOrderChange={handleRowOrderChange}
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={getDetailPanelHeight}
              sx={{
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
                  { outline: 'none' },
              }}
              autoHeight
              loading={isLoading}
              localeText={{
                noRowsLabel: 'Keine Positionen für diesen Vertrag vorhanden',
              }}
            />
          </TabPanel>
          <TabPanel value="documents" sx={{ p: 0, mt: 2 }}>
            {isDocsPending ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : documentFiles && documentFiles.length > 0 ? (
              <DataGridPro
                rows={[...documentFiles].sort(
                  (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
                )}
                getRowId={(row) => row?._id}
                columns={[
                  {
                    field: 'filename',
                    headerName: 'Dateiname',
                    flex: 1,
                    minWidth: 200,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    renderCell: (params) => (
                      <Typography noWrap>
                        {params?.value?.split('/').pop()}
                      </Typography>
                    ),
                  },
                  {
                    field: 'size',
                    headerName: 'Größe',
                    width: 120,
                    valueGetter: (size) => formatBytes(size ?? 0),
                  },
                  {
                    field: 'createdAt',
                    headerName: 'Datum',
                    width: 150,
                    renderCell: (params) =>
                      new Date(params.value).toLocaleDateString('de-DE'),
                  },
                  {
                    field: 'download',
                    headerName: '',
                    width: 70,
                    sortable: false,
                    filterable: false,
                    renderCell: (params) => (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadFile(params.row);
                        }}
                        disabled={downloading[params.row?._id]}
                      >
                        {downloading[params.row?._id] ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DownloadIcon />
                        )}
                      </IconButton>
                    ),
                  },
                  {
                    field: 'delete',
                    headerName: '',
                    width: 70,
                    sortable: false,
                    filterable: false,
                    renderCell: (params) => (
                      <IconButton
                        color="error"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleDeleteFile(params.row?._id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ),
                  },
                ]}
                autoHeight
                disableRowSelectionOnClick
                rowHeight={60}
                localeText={{ noRowsLabel: 'Keine Dokumente vorhanden' }}
                sx={{
                  '& .MuiDataGrid-cell': { overflow: 'visible' },
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                }}
              />
            ) : (
              <Typography sx={{ p: 2, color: 'text.secondary' }}>
                Keine Dokumente vorhanden.
              </Typography>
            )}
          </TabPanel>
          <TabPanel value="tickets" sx={{ p: 0, mt: 2 }}>
            {contract?.linkedDocuments?.length > 0 ? (
              <DataGridPro
                rows={contract?.linkedDocuments || []}
                columns={[
                  { field: 'type', headerName: 'Typ', width: 120 },
                  {
                    field: 'id',
                    headerName: 'ID',
                    width: 100,
                    renderCell: (params) => <a>#{params.value}</a>,
                  },
                  {
                    field: 'title',
                    headerName: 'Titel',
                    flex: 1,
                    minWidth: 200,
                  },
                  {
                    field: 'description',
                    headerName: 'Beschreibung',
                    flex: 2,
                    minWidth: 300,
                  },
                ]}
                autoHeight
                disableRowSelectionOnClick
                localeText={{
                  noRowsLabel: 'Keine Tickets vorhanden',
                }}
              />
            ) : (
              <Typography sx={{ p: 2, color: 'text.secondary' }}>
                Keine Tickets vorhanden.
              </Typography>
            )}
          </TabPanel>
          <TabPanel value="comments" sx={{ p: 0, mt: 2 }}>
            <Comments
              entityId={contract?._id}
              entityName="contract"
              ref={commentsRef}
            />
          </TabPanel>
          <TabPanel value="history" sx={{ p: 0, mt: 2 }}>
            <History documentId={contract?._id} />
          </TabPanel>
        </TabContext>
      </Card>

      {selectedFormState && selectedEditItem !== null && !isSupportTicket && (
        <ContentEditDialog
          editingItemUuid={!isSalesBOMItem && selectedEditItem}
          handleCloseDialog={() => {
            setSelectedEditItem(null);
            setIsSalesBOMItem(false);
          }}
          formState={selectedFormState}
          setFormState={setSelectedFormState}
          sourceOptions={combinedSourceOptions}
          handleSave={handleSave}
          contractItems={contract.contractItems || []}
          customerId={customerId}
          isSalesBOMItem={isSalesBOMItem}
          groupNames={contract.groupNames ?? []}
          deleteItem={deleteItem}
        />
      )}
      {selectedFormState && selectedEditItem !== null && isSupportTicket && (
        <SupportTicketDialog
          itemUuid={selectedEditItem}
          rows={rows}
          handleCloseDialog={() => {
            setSelectedEditItem(null);
            setIsSupportTicket(false);
            setIsSalesBOMItem(false);
          }}
          contract={contract}
          isSalesBOMItem={isSalesBOMItem}
          addLinkedDocumentToContract={addLinkedDocumentToContract}
        />
      )}

      <Dialog
        open={isDocsDialogOpen}
        onClose={() => !isUploading && setIsDocsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Dokumente hochladen</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: 350, width: '100%', mb: 2 }}>
            <DataGridPro
              rows={uploadRows}
              columns={[
                {
                  field: 'originalName',
                  headerName: 'Original Dateiname',
                  flex: 1,
                  minWidth: 200,
                },
                {
                  field: 'targetName',
                  headerName: 'Ziel-Dateiname',
                  flex: 1,
                  minWidth: 200,
                  display: 'flex',
                  justifyContent: 'center',
                  renderCell: (params) => (
                    <Typography
                      variant="body2"
                      color={
                        params.row.action === 'rename'
                          ? 'primary'
                          : 'textPrimary'
                      }
                    >
                      {params.value}
                    </Typography>
                  ),
                },
                {
                  field: 'status',
                  headerName: 'Aktion / Status',
                  width: 320,
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  renderCell: (params) => {
                    const { hasCollision } = params.row;
                    if (!hasCollision) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'success.main',
                          }}
                        >
                          <CheckIcon fontSize="small" sx={{ mr: 1 }} /> Neu
                        </Box>
                      );
                    }
                    return (
                      <FormControl fullWidth size="small">
                        <Select
                          value={params.row.action}
                          onChange={(e) =>
                            updateRowAction(params.row.id, e.target.value)
                          }
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                            },
                          }}
                        >
                          <MenuItem value="rename">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <WarningIcon
                                fontSize="small"
                                color="warning"
                                sx={{ mr: 1 }}
                              />
                              Automatisch umbenennen
                            </Box>
                          </MenuItem>
                          <MenuItem value="overwrite">Überschreiben</MenuItem>
                        </Select>
                      </FormControl>
                    );
                  },
                },

                {
                  field: 'size',
                  headerName: 'Größe',
                  width: 100,
                  valueGetter: (size) => formatBytes(size ?? 0),
                },
                {
                  field: 'delete',
                  headerName: '',
                  width: 70,
                  sortable: false,
                  renderCell: (params) => (
                    <IconButton
                      color="error"
                      onClick={() => removeUploadRow(params.row.id)}
                      disabled={isUploading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  ),
                },
              ]}
              disableRowSelectionOnClick
              hideFooter
              rowHeight={60}
              localeText={{ noRowsLabel: 'Keine Dateien ausgewählt' }}
              sx={{
                '& .MuiDataGrid-cell': { overflow: 'visible' },
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
              }}
            />
          </Box>

          <Box
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : '#ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: isDragActive ? '#f0f8ff' : '#fafafa',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              opacity: isUploading ? 0.6 : 1,
              transition: 'all 0.2s',
            }}
          >
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
              disabled={isUploading}
            />
            <CloudUploadIcon
              sx={{
                fontSize: 40,
                color: isDragActive ? 'primary.main' : 'text.secondary',
                mb: 1,
              }}
            />
            <Typography
              variant="h6"
              color={isDragActive ? 'primary.main' : 'text.secondary'}
            >
              {isDragActive
                ? 'Dateien hier loslassen'
                : 'Dateien hierher ziehen oder klicken zum Auswählen'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDocsDialogOpen(false)}
            disabled={isUploading}
            color="inherit"
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={processUploads}
            disabled={uploadRows.length === 0 || isUploading}
            startIcon={
              isUploading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isUploading ? 'Wird hochgeladen...' : 'Hochladen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
